import { ROTULO_DO_ALERTA, textoDaResposta, visivel, type AlertaDaAnamnese, type ModeloDeAnamnese, type RespostasDaAnamnese } from "@bookdental/schemas";
import { esc } from "@/components/production/impressao";

export type DadosDaImpressao = {
  clinica: { nome: string; documento: string | null; telefone: string | null; endereco: string };
  paciente: { name: string; document: string | null; birthDate: Date | string | null };
  modelo: ModeloDeAnamnese;
  respostas: RespostasDaAnamnese;
  alertas: string[];
  respondidaEm: Date | string | null;
  respondidaPor: string | null;
  responsavelNome: string | null;
  responsavelParentesco: string | null;
  assinatura: { nome: string; ip: string | null; aparelho: string | null; hash: string | null; cienciaEm: Date | string | null } | null;
  revisadaEm: Date | string | null;
  revisadaPorNome: string | null;
  observacaoDaRevisao: string | null;
  adendos: { texto: string; autorNome: string | null; criadoEm: Date | string }[];
};

const quando = (d: Date | string | null) => (d ? new Date(d).toLocaleString("pt-BR") : "—");
const dia = (d: Date | string | null) => (d ? new Date(d).toLocaleDateString("pt-BR", { timeZone: "UTC" }) : "—");

/** Anamnese A4: respostas por seção, alertas, as evidências da assinatura, a revisão e os adendos. */
export function htmlDaAnamnese(d: DadosDaImpressao) {
  const secoes = d.modelo.secoes
    .map((s) => {
      const linhas = s.perguntas
        .filter((p) => visivel(p, d.respostas))
        .map((p) => `<tr><td>${esc(p.texto)}</td><td>${esc(textoDaResposta(p, d.respostas[p.id]) ?? "Sem resposta")}</td></tr>`)
        .join("");
      return `<h2>${esc(s.titulo)}</h2><table>${linhas}</table>`;
    })
    .join("");
  const quem =
    d.respondidaPor === "RESPONSAVEL"
      ? `Responsável legal: ${esc(d.responsavelNome ?? "")}${d.responsavelParentesco ? ` (${esc(d.responsavelParentesco)})` : ""}`
      : d.respondidaPor === "EQUIPE"
        ? "Preenchida pela equipe da clínica com o paciente"
        : "O próprio paciente";
  const a = d.assinatura;
  return `<div class="pg">
<header><div><h1>${esc(d.clinica.nome)}</h1><p>${[d.clinica.documento, d.clinica.telefone].filter(Boolean).map((x) => esc(x!)).join(" · ")}</p><p>${esc(d.clinica.endereco)}</p></div>
<div class="num"><b>Anamnese</b><span>${quando(d.respondidaEm)}</span></div></header>
<section class="pac"><p><b>Paciente:</b> ${esc(d.paciente.name)}${d.paciente.document ? ` · ${esc(d.paciente.document)}` : ""}</p>
<p><b>Nascimento:</b> ${dia(d.paciente.birthDate)} · <b>Respondida por:</b> ${quem}</p>
${d.alertas.length ? `<p class="al"><b>Alertas:</b> ${d.alertas.map((x) => esc(ROTULO_DO_ALERTA[x as AlertaDaAnamnese] ?? x)).join(" · ")}</p>` : ""}</section>
${secoes}
${
  a
    ? `<h2>Assinatura eletrônica</h2><p class="ev">Assinado por <b>${esc(a.nome)}</b> em ${quando(d.respondidaEm)}${a.ip ? ` · IP ${esc(a.ip)}` : ""}${a.cienciaEm ? " · ciência do aviso de privacidade registrada" : ""}.<br>Código de integridade (SHA-256): ${esc(a.hash ?? "—")}</p>`
    : ""
}
${
  d.revisadaEm
    ? `<h2>Revisão</h2><p>Revisada por <b>${esc(d.revisadaPorNome ?? "—")}</b> em ${quando(d.revisadaEm)}.${d.observacaoDaRevisao ? `<br>${esc(d.observacaoDaRevisao)}` : ""}</p>`
    : ""
}
${d.adendos.length ? `<h2>Adendos</h2>${d.adendos.map((x) => `<p>${quando(x.criadoEm)} · ${esc(x.autorNome ?? "—")}: ${esc(x.texto)}</p>`).join("")}` : ""}
<div class="ass"><div>Paciente ou responsável</div><div>Cirurgião-dentista</div></div>
</div>`;
}

export const CSS_DA_ANAMNESE = `
@page { size: A4; margin: 14mm; }
.pg { font-size: 11.5px; }
header { display: flex; justify-content: space-between; border-bottom: 2px solid #000; padding-bottom: 8px; }
h1 { font-size: 17px; margin: 0 0 4px; } header p { margin: 1px 0; color: #333; }
.num { text-align: right; } .num b { display: block; font-size: 14px; } .num span { color: #333; }
.pac { margin: 12px 0; } .pac p { margin: 2px 0; } .al { color: #92400e; }
h2 { font-size: 12.5px; margin: 14px 0 4px; border-bottom: 1px solid #999; padding-bottom: 2px; break-after: avoid; }
table { width: 100%; border-collapse: collapse; } td { border-bottom: 1px solid #ddd; padding: 4px; vertical-align: top; width: 50%; }
tr { break-inside: avoid; } .ev { font-size: 10.5px; color: #333; word-break: break-all; }
.ass { display: flex; gap: 40px; margin-top: 60px; } .ass div { flex: 1; border-top: 1px solid #000; padding-top: 4px; text-align: center; }
`;
